import { BellSimpleIcon } from '@phosphor-icons/react';
import { useQueryClient } from '@tanstack/react-query';
import { Popover } from 'antd';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useToast } from '@/components/feedback/toast';
import { useRealtimeChannel } from '@/hooks/use-realtime-channel';

import { resolveDeepLink } from '../format';
import { notificationKeys, useMarkAllRead, useMarkRead, useNotifications } from '../hooks';
import type { AppNotification } from '../types';

import styles from './notification-bell.module.scss';
import { NotificationPanel } from './notification-panel';

/**
 * Header bell + dropdown notification center (story 101848). Opening the panel marks
 * all unread as read; the bell shows a red dot while any unread remain. New
 * notifications arriving on the `notifications` realtime channel invalidate the feed
 * so they appear at the top and bump the badge without the panel being reopened.
 * Tapping a row marks it read and deep-links by type; an unresolvable target shows a
 * toast and does not navigate.
 */
export function NotificationBell() {
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [open, setOpen] = useState(false);

  const query = useNotifications();
  const markAllRead = useMarkAllRead();
  const markRead = useMarkRead();

  const notifications = query.data?.data ?? [];
  const unread = query.data?.meta.unread ?? 0;
  const hasUnread = unread > 0;

  // Live arrival: re-fetch the feed so a new unread lands at the top + bumps the badge.
  useRealtimeChannel<AppNotification>('notifications', () => {
    void queryClient.invalidateQueries({ queryKey: notificationKeys.list() });
  });

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    // Mark visible unread as read when the panel CLOSES — so the unread treatment
    // (green avatar + dot, cream row) stays visible while open, while still honoring
    // the AC that "visiting the center marks visible unread as read".
    if (!next && hasUnread && !markAllRead.isPending) {
      markAllRead.mutate();
    }
  };

  const handleSelect = (n: AppNotification) => {
    if (!n.read) markRead.mutate(n.id);

    const resolution = resolveDeepLink(n);
    if ('error' in resolution) {
      toast.error({ title: 'Unable to open', description: resolution.error });
      return;
    }

    setOpen(false);
    navigate(resolution.href);
  };

  return (
    <Popover
      open={open}
      onOpenChange={handleOpenChange}
      trigger="click"
      placement="bottomRight"
      arrow={false}
      styles={{ content: { padding: 0 } }}
      content={
        <NotificationPanel
          notifications={notifications}
          isLoading={query.isLoading}
          isError={query.isError}
          onRetry={() => void query.refetch()}
          onSelect={handleSelect}
        />
      }
    >
      <button
        type="button"
        className={styles.bell}
        aria-label={hasUnread ? `Notifications, ${unread} unread` : 'Notifications'}
      >
        <BellSimpleIcon size={20} />
        {hasUnread ? <span className={styles.badge} aria-hidden="true" /> : null}
      </button>
    </Popover>
  );
}
